import { useTableFilter, useTableServerSideFiltering } from '@/shared/hooks';
import GeneralTable from '@/shared/components/ui/GeneralTable';
import { Grid, IconButton, Tooltip } from '@mui/material';
import {
  ROUTER_PATHS,
  TABLE_CONSTANTS,
  formatDate,
} from '@/shared';
import { useNavigate } from 'react-router-dom';
import { Timeline, Visibility } from '@mui/icons-material';
import { useFetchGetfactibilidadCorporativos } from '@/shared/actions/erp/factibilidad_corporativo/factibilidad_corporativo.actions';
import { IFactibilidadCorporativo } from '@/shared/interfaces/erp/factibilidad_corporativo';
import { MRT_ColumnDef } from 'material-react-table';
import { useEffect, useMemo, useState } from 'react';
import { EstadoComponente } from '@/shared/components/ui';
import InfoUser from '@/shared/components/ui/InfoUser/InfoUser';
import dayjs from 'dayjs';
import { useAuditoriaTableStore } from '@/store/erp/noc/auditoria-table.store';




export default function TableFactibilidadAprobada() {

  const navigate = useNavigate();
  const setAuditoriaState = useAuditoriaTableStore(s => s.setState)
  const snapshot = useAuditoriaTableStore((s) => s);
  const clear = useAuditoriaTableStore(s => s.clear)
  //
  const [rowSelection, setRowSelection] = useState({})
  // Función para obtener el color según el estado de factibilidad
  const getColorByEstado = (estado: string | undefined) => {
    switch (estado) {
      case 'APROBADO':
        return 'green';
      case 'PENDIENTE':
        return 'orange';
      case 'EN_INSPECCION':
        return 'blue';
      case 'INSPECCIONADO':
        return '#2196F3';
      case 'EN_GESTION':
        return '#9C27B0';
      case 'PRE_RECHAZADO':
        return '#FF5722';
      case 'CANCELADO':
        return 'red';
      default:
        return 'gray';
    }
  };

  // Columnas para auditoría de todas las factibilidades
  const columnsFactibilidadAprobada = useMemo<MRT_ColumnDef<IFactibilidadCorporativo>[]>(() => [
    {
      accessorKey: 'razon_social',
      header: 'Razón Social',
      Cell: ({ row }) => row.original?.prospecto_corporativo_data?.razon_social || "N/A"
    },
    {
      accessorKey: 'identificacion',
      header: 'Identificación',
      Cell: ({ row }) => row.original?.prospecto_corporativo_data?.identificacion || "N/A"
    },
    {
      accessorKey: 'numero_referencia',
      header: 'Número Referencia',
      Cell: ({ row }) => row.original?.numero_referencia || "N/A"
    },
    {
      id: 'nombre_personalizado',
      accessorKey: 'nombre_enlace',
      header: 'Nombre Enlace',
      //enableColumnFilter: false,
      Cell: ({ row }) => {
        const nombreBase = row.original?.preventa_corporativa_data?.data_plan_personalizado?.nombre_personalizado;
        if (!nombreBase) return "N/A";
        return row.original?.es_backup ? `${nombreBase}_BACKUP` : nombreBase;
      },
    },
    {
      accessorKey: 'estado_factibilidad',
      header: 'Estado Factibilidad',
      size: 170,
      filterVariant: 'select',
      filterSelectOptions: [
        { label: 'Pendiente', value: 'PENDIENTE' },
        { label: 'En Inspección', value: 'EN_INSPECCION' },
        { label: 'Inspeccionado', value: 'INSPECCIONADO' },
        { label: 'En Gestión', value: 'EN_GESTION' },
        { label: 'Aprobado', value: 'APROBADO' },
        { label: 'Pre-Rechazado', value: 'PRE_RECHAZADO' },
        { label: 'Cancelado', value: 'CANCELADO' },
      ],
      Cell: ({ row }) => (
        <EstadoComponente
          backgroundColor={getColorByEstado(row.original?.estado_factibilidad)}
          texto={row.original?.estado_factibilidad || 'N/A'}
        />
      ),
    },
    {
      accessorKey: 'preaprobado_inventario',
      header: 'Pre-Aprob. Inventario',
      size: 150,
      filterVariant: 'select',
      filterSelectOptions: [
        { label: 'Sí', value: 'true' },
        { label: 'No', value: 'false' },
      ],
      Cell: ({ row }) => (
        <EstadoComponente
          backgroundColor={row.original?.preaprobado_inventario ? 'green' : 'gray'}
          texto={row.original?.preaprobado_inventario ? 'SÍ' : 'NO'}
        />
      ),
    },
    {
      accessorKey: 'aprobado_inventario',
      header: 'Aprob. Inventario',
      size: 150,
      filterVariant: 'select',
      filterSelectOptions: [
        { label: 'Sí', value: 'true' },
        { label: 'No', value: 'false' },
      ],
      Cell: ({ row }) => (
        <EstadoComponente
          backgroundColor={row.original?.aprobado_inventario ? 'green' : 'gray'}
          texto={row.original?.aprobado_inventario ? 'SÍ' : 'NO'}
        />
      ),
    },
    {
      accessorKey: 'aprobado_supervisor',
      header: 'Aprob. Supervisor',
      size: 150,
      filterVariant: 'select',
      filterSelectOptions: [
        { label: 'Sí', value: 'true' },
        { label: 'No', value: 'false' },
      ],
      Cell: ({ row }) => (
        <EstadoComponente
          backgroundColor={row.original?.aprobado_supervisor ? 'green' : 'gray'}
          texto={row.original?.aprobado_supervisor ? 'SÍ' : 'NO'}
        />
      ),
    },
    {
      accessorKey: 'aceptado_factibilidad',
      header: 'Aprob. NOC',
      size: 130,
      filterVariant: 'select',
      filterSelectOptions: [
        { label: 'Sí', value: 'true' },
        { label: 'No', value: 'false' },
      ],
      Cell: ({ row }) => (
        <EstadoComponente
          backgroundColor={row.original?.aceptado_factibilidad ? 'green' : 'gray'}
          texto={row.original?.aceptado_factibilidad ? 'SÍ' : 'NO'}
        />
      ),
    },
    {
      accessorKey: 'user_aprueba',
      header: 'Último Aprobador',
      enableColumnFilter: false,
      Cell: ({ row }) => row.original?.user_aprueba ? (
        <InfoUser user={row.original.user_aprueba} />
      ) : "N/A"
    },
    {
      accessorKey: 'fecha_factibilidad',
      header: 'Fecha Últ. Aprobación',
      enableColumnFilter: false,
      Cell: ({ row }) => row.original?.fecha_factibilidad
        ? dayjs(row.original.fecha_factibilidad).format("DD/MM/YYYY HH:mm")
        : "N/A"
    },
    {
      accessorKey: 'es_backup',
      header: 'Tipo',
      filterVariant: 'select',
      filterSelectOptions: [
        { label: 'Principal', value: 'false' },
        { label: 'Backup', value: 'true' },
      ],
      Cell: ({ row }) => (
        <EstadoComponente
          backgroundColor={row.original?.es_backup ? '#FF9800' : '#4CAF50'}
          texto={row.original?.es_backup ? 'BACKUP' : 'PRINCIPAL'}
        />
      ),
    },
    {
      accessorKey: 'fecha_creacion',
      header: 'Fecha Creación',
      enableColumnFilter: false,
      Cell: ({ row }) => formatDate(row.original?.fecha_creacion || "")
    },
    {
      accessorKey: 'fecha_modificacion',
      header: 'Fecha Modificación',
      enableColumnFilter: false,
      Cell: ({ row }) => formatDate(row.original?.fecha_modificacion || "")
    },
  ], []);


  // Hooks de filtrado y paginación
  const {
    pagination,
    setPagination,
  } = useTableFilter();

  const {
    filterObject,
    columnFilters,
    setColumnFilters } = useTableServerSideFiltering();




  // Fetch de datos - todas las factibilidades sin filtros de estado
  const {
    isLoading,
    data: dataFactibilidadCorporativa,
    isRefetching
  } = useFetchGetfactibilidadCorporativos(
    {
      page: pagination.pageIndex + 1,
      page_size: pagination.pageSize,
      ...filterObject,
      filterByState: false,
    },
    ///!data.length // Solo hacer fetch si no hay datos en Zustand
  );


  useEffect(() => {

    if (!snapshot.hasSnapshot) return;

    setPagination({
      pageIndex: snapshot.page,
      pageSize: snapshot.pageSize,
    });

    setColumnFilters(snapshot.columnFilters);
    clear();
  }, []);

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <GeneralTable<IFactibilidadCorporativo>
            columns={columnsFactibilidadAprobada}
            data={dataFactibilidadCorporativa?.data?.data || []}
            isLoading={isLoading}
            isRefetching={isRefetching}
            enableGlobalFilter={false}
            pagination={pagination}
            onPaging={setPagination}
            rowCount={dataFactibilidadCorporativa?.data?.count}
            enableManualFiltering={true}
            columnFilters={columnFilters}
            onColumnFiltersChange={setColumnFilters}
            enableSorting={false}
            canDelete={false}
            canEdit={false}
            enableSelectAll
            enableRowSelection
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            pinLeftColumnIndexes={[0,1,2]}
            positionActionsColumn='first'
            actionsColumnSize={TABLE_CONSTANTS.ACTIONCOLUMN_WIDTH_MEDIUM}
            enableEditing={true}
            showCustomActionBtnSpace={true}
            customActionBtnSpace={(value) => {
              // Determinar la navegación según el estado del registro (SOLO LECTURA desde Auditoría)
              const handleNavigateToFactibilidad = () => {
                // Si ya fue aceptada por NOC, ir a visualización de factibilidad (solo lectura)
                if (value.aceptado_factibilidad) {
                  navigate(
                    ROUTER_PATHS.noc.factibilidadCorporativoView.replace(
                      ':idfactibilidadcorporativa',
                      value.id_factibilidad_coorporativa
                    )
                  );
                } else {
                  // Si aún no ha sido aceptada, ir a visualización de pre-factibilidad (solo lectura)
                  navigate(
                    ROUTER_PATHS.noc.preFactibilidadCorporativoView.replace(
                      ':idpreventacorporativa',
                      value.preventa_corporativa_data?.id_preventa_coorporativa || ''
                    )
                  );
                }
              };

              return (
                <Grid container spacing={1}>
                  <Grid item>
                    <Tooltip
                      placement="top"
                      title={
                        value.aceptado_factibilidad
                          ? 'Ver Factibilidad'
                          : 'Ver Pre-Factibilidad'
                      }
                    >
                      <IconButton onClick={handleNavigateToFactibilidad}>
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  <Grid item>
                    <Tooltip placement="top" title="Ver Trazabilidad">
                      <IconButton
                        color="primary"
                        onClick={() => {
                          setAuditoriaState({
                            page: pagination.pageIndex,
                            pageSize: pagination.pageSize,
                            columnFilters: columnFilters,
                          });
                          navigate(
                            `${ROUTER_PATHS.noc.trazabilidadFactibilidadCorporativoNav}/${value.id_factibilidad_coorporativa}?from=audit`
                          );
                        }}
                      >
                        <Timeline />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              );
            }}
          />
        </Grid>
      </Grid>
    </>
  );
}
